import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Segmented, Loading, int, plural } from "@/components/common";
import { cn } from "@/lib/utils";
import { ArrowLeft, TriangleAlert, CircleAlert, RotateCw, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { DocTag } from "@/components/DocumentsView";
import { Disclosure } from "@/components/ChangeReport";
import RedlinePage, { RedlineLegend } from "@/components/RedlinePage";
import { docName, docFolderLine, isMarkdown } from "@/lib/workKinds";

// design_handoff_crewbench/screens/Session Workspace.dc.html, the diff viewer (README 14a); docs/DIFF_VIEWER_PLAN.md.
// The branch's changes for the session's independent professional, while it's unlocked: the runner works them out
// from the worktree and the backend passes them through (GET /sessions/{id}/diff, /diff/file, with the unlock token).
// Nothing is kept: the queries live under ["diff", id], which the workspace drops when the session locks or ends.

const STATUS_TONE = { added: "text-gr-success", modified: "text-gr-warning", deleted: "text-gr-danger" };
const turnsLabel = (turns) => (turns.length === 1 ? `Turn ${turns[0]}` : turns.length ? `Turns ${turns.join(", ")}` : "");
// the documents view's words (design_handoff_documents_view/screens/Document Changes.dc.html): versions, not turns
const DOC_STATE = { added: "new", modified: "changed", deleted: "removed" };
const versionsLabel = (ns) => (ns.length === 1 ? `version ${ns[0]}` : `versions ${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`);

// the files grouped by folder, as the design's tree: a folder row ("src/checkout/") then its files
function treeOf(files) {
  const rows = [];
  let dir = null;
  for (const f of files) {
    const cut = f.path.lastIndexOf("/");
    const d = f.path.slice(0, cut + 1);
    if (d !== dir) { if (d) rows.push({ dir: d }); dir = d; }
    rows.push({ file: f, label: f.path.slice(cut + 1) });
  }
  return rows;
}

// each line with its old and new numbers, worked out from its hunk's start
function numbered(hunk) {
  let o = hunk.old_start;
  let n = hunk.new_start;
  return hunk.lines.map(([sign, text]) => {
    if (sign === " ") return { sign, text, old: o++, nw: n++ };
    if (sign === "+") return { sign, text, nw: n++ };
    if (sign === "-") return { sign, text, old: o++ };
    return { sign, text };  // "\ No newline at end of file"
  });
}

const ROW = "grid grid-cols-[48px_48px_20px_minmax(0,1fr)]";
const NUM = "pr-2.5 text-right text-muted-foreground select-none";

// a file's hunks as numbered lines (the Lines view, and the only view for code)
function LinesView({ d }) {
  return (
    <div className="font-mono text-[12px] leading-5 min-w-[600px] pb-3" dir="ltr" data-testid="diff-lines">
      {d.replaced && <div className="px-4 py-1 text-muted-foreground font-sans">Shown with unknown characters replaced.</div>}
      {!d.hunks.length && (d.meta.length ? d.meta : ["No line changes."]).map((m) => (
        <div key={m} className="px-4 py-[3px] text-muted-foreground bg-card whitespace-pre">{m}</div>
      ))}
      {d.hunks.map((h, hi) => (
        <div key={hi}>
          <div className="px-4 py-[3px] text-muted-foreground bg-card whitespace-pre">{h.header}</div>
          {numbered(h).map((l, i) => l.sign === "\\" ? (
            <div key={i} className="px-4 text-muted-foreground whitespace-pre">\ {l.text.trim()}</div>
          ) : (
            <div key={i} className={cn(ROW, l.sign === "+" && "bg-[var(--gr-diff-add)]", l.sign === "-" && "bg-[var(--gr-diff-del)]")}>
              <span className={NUM}>{l.old ?? ""}</span>
              <span className={NUM}>{l.nw ?? ""}</span>
              <span className={l.sign === "+" ? "text-gr-success" : l.sign === "-" ? "text-gr-danger" : ""}>{l.sign === "-" ? "−" : l.sign.trim()}</span>
              <span className="whitespace-pre pr-4">{l.text}</span>
            </div>
          ))}
        </div>
      ))}
      {d.truncated && (
        <div className="flex items-center gap-1.5 px-4 pt-3 font-sans text-sm text-gr-warning">
          <TriangleAlert className="w-3.5 h-3.5 shrink-0" />This diff is too large to show in full. The rest is on the branch.
        </div>
      )}
    </div>
  );
}

// the documents view's error: their work is safe, only the view failed
function DocError({ error, onRetry, onLines }) {
  const old = error?.response?.status === 409;
  return (
    <div role="alert" className="m-4 max-w-[520px] bg-card rounded-[10px] px-5 py-[18px] flex flex-col items-start gap-2.5" data-testid="diff-error">
      <span className="flex items-center gap-2 font-semibold text-gr-danger"><CircleAlert className="w-4 h-4" />{old ? "No page view here yet" : "Couldn't load the changes"}</span>
      <span className="text-gr-ink-secondary [text-wrap:pretty]">{old ? `${apiErr(error)}. The line view works meanwhile.` : "Your documents are saved. Only this view didn't load. Try again, or go back to the conversation."}</span>
      {old ? <Button variant="outline" onClick={onLines}>Show lines</Button> : <Button variant="outline" onClick={onRetry}><RotateCw />Try again</Button>}
    </div>
  );
}

// Technical details under a document: its path, the branch and each version's commit ID, to copy
function TechDetails({ path, branch, shas }) {
  const copy = async (label, value) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied.`); }
    catch { toast.error("Couldn't copy. Select the text and copy it yourself."); }
  };
  const rows = [["File", path], ...(branch ? [["Branch", branch]] : []), ...shas.map(([n, sha]) => [`Version ${n}`, sha])];
  return (
    <div className="mx-4 mb-4 max-w-[720px]">
      <Disclosure testid="diff-technical-details">
        <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="m-0 flex items-center gap-1.5 min-w-0">
                <span dir="ltr" className="font-mono text-[11px] [overflow-wrap:anywhere]">{v}</span>
                <button type="button" title="Copy" aria-label={`Copy ${k.toLowerCase()}`} onClick={() => copy(k, v)}
                  className="w-6 h-6 shrink-0 grid place-items-center rounded-md text-muted-foreground hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                  <Copy className="w-3 h-3" /></button>
              </dd>
            </Fragment>
          ))}
        </dl>
      </Disclosure>
    </div>
  );
}

// One file in a scope. Code: its lines. A document: its name and state, and for Markdown the Page view (the redline)
// or its lines; `mode` page|lines and `scope` changes|whole are kept by the viewer across documents.
function FileDiff({ sid, token, turn, file, documents, versionOf, mode, onMode, scope, onScope, branch, turns }) {
  const md = documents && isMarkdown(file.path) && !file.binary;
  const asPage = md && mode === "page";
  const lines = useQuery({
    queryKey: ["diff", sid, "file", turn, file.path], staleTime: 30000, enabled: !asPage,
    queryFn: async () => (await api.get(`/sessions/${sid}/diff/file`, {
      params: { path: file.path, turn }, headers: { "X-Unlock-Token": token } })).data,
  });
  const page = useQuery({
    queryKey: ["diff", sid, "page", turn, file.path], staleTime: 30000, enabled: asPage, retry: false,
    queryFn: async () => (await api.get(`/sessions/${sid}/diff/page`, {
      params: { path: file.path, turn }, headers: { "X-Unlock-Token": token } })).data,
  });
  const q = asPage ? page : lines;
  const d = q.data;
  const ns = (file.turns || []).map(versionOf);
  const shas = (file.turns || []).map((tn) => [versionOf(tn), (turns || []).find((x) => x.turn === tn)?.sha]).filter(([, sha]) => sha);
  return (
    <>
      <div className="sticky top-0 left-0 z-[1] flex flex-wrap justify-between items-center gap-x-3 gap-y-2 px-4 py-[9px] bg-background border-b text-sm"
        data-testid="diff-file-header">
        {documents ? (
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0">
            <span className="font-display text-[15px] tracking-[0.02em] font-semibold [overflow-wrap:anywhere]">{docName(file.path)}</span>
            <DocTag state={DOC_STATE[file.status]} />
            {ns.length > 0 && <span className="text-muted-foreground">{file.status === "added" ? "New" : "Changed"} in {versionsLabel(ns)}</span>}
          </span>
        ) : (
          <span className="flex items-center gap-2 min-w-0">
            <span className={STATUS_TONE[file.status] || "text-muted-foreground"}>{file.status}</span>
            <span className="font-mono [overflow-wrap:anywhere]">{file.path}</span>
          </span>
        )}
        {md ? (
          <span className="flex flex-wrap gap-2">
            <Segmented label="Show as" options={[["page", "Page"], ["lines", "Lines"]]} value={mode} onChange={onMode} testid="diff-mode" className="[&>button]:h-[26px] [&>button]:px-2.5 [&>button]:text-[12px]" />
            {asPage && file.status === "modified" && (
              <Segmented label="How much to show" options={[["changes", "Changes only"], ["whole", "Whole document"]]} value={scope} onChange={onScope}
                testid="diff-scope" className="[&>button]:h-[26px] [&>button]:px-2.5 [&>button]:text-[12px]" />
            )}
          </span>
        ) : (
          <span className="text-muted-foreground whitespace-nowrap tabular-nums">
            {!documents && turnsLabel(file.turns) && `${turnsLabel(file.turns)} · `}
            <span className="text-gr-success">+{int(file.added)}</span><span className="text-gr-danger"> −{int(file.removed)}</span>
          </span>
        )}
      </div>
      {q.isLoading ? <div className="p-4"><Loading label="Loading the changes" /></div> : q.isError ? (
        documents ? <DocError error={q.error} onRetry={() => q.refetch()} onLines={() => onMode("lines")} />
          : <div className="p-4 text-sm text-gr-danger" data-testid="diff-error">{apiErr(q.error)}</div>
      ) : d.binary ? (
        <div className={documents ? "m-4 max-w-[520px] bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-1" : "p-4 text-sm text-muted-foreground"} data-testid="diff-binary">
          {documents ? <>
            <span className="font-semibold">No preview for this kind of file yet.</span>
            <span className="text-sm text-muted-foreground">{docName(file.path)} is saved in {versionsLabel(ns) || "this session"} and goes into the change report.</span>
          </> : "Binary file, not shown."}
        </div>
      ) : asPage ? (
        <div className="p-4 sm:p-5 flex flex-col gap-3.5">
          <RedlineLegend />
          <RedlinePage page={d} onlyChanges={scope === "changes"} name={docName(file.path)} />
        </div>
      ) : documents ? (
        <div className="m-4 border rounded-lg overflow-auto max-w-[960px]">
          <div className="flex justify-end gap-2.5 px-3.5 py-1.5 border-b text-sm tabular-nums">
            <span className="text-gr-success">+{int(file.added)}</span><span className="text-gr-danger">−{int(file.removed)}</span>
          </div>
          <LinesView d={d} />
        </div>
      ) : <LinesView d={d} />}
      {documents && !q.isLoading && <TechDetails path={file.path} branch={branch} shas={shas} />}
    </>
  );
}

// `documents`: the documents view's words, with `versions` (lib/workKinds.versionsOf) to name each turn by its version
export default function DiffViewer({ sid, token, turn, onTurn, path, onPath, onClose, documents, versions, branch }) {
  // the documents view opens Markdown as a page (owner's decision 2026-10-13), the whole document by default
  const [mode, setMode] = useState("page");
  const [scope, setScope] = useState("whole");
  const q = useQuery({
    queryKey: ["diff", sid, "index", turn], staleTime: 30000,
    queryFn: async () => (await api.get(`/sessions/${sid}/diff`, { params: { turn }, headers: { "X-Unlock-Token": token } })).data,
  });
  const index = q.data;
  const files = index?.files || [];
  const current = files.find((f) => f.path === path) || files[0];
  const byTurn = Object.fromEntries((versions || []).map((v) => [v.turn, v.n]));
  const versionOf = (t) => byTurn[t] ?? (index?.turns || []).findIndex((x) => x.turn === t) + 1;
  const turnTabs = documents
    ? [["all", "All versions"], ...(index?.turns || []).map((t) => [String(t.turn), `Version ${versionOf(t.turn)}`])]
    : [["all", "All turns"], ...(index?.turns || []).map((t) => [String(t.turn), `Turn ${t.turn}`])];
  return (
    // in the documents view on a phone, the page scrolls as one: the viewer fills the screen, the side panel follows
    <div className={cn("min-w-0 flex flex-col", documents ? "shrink-0 h-[calc(100dvh-48px)] lg:h-auto lg:shrink lg:flex-1 lg:min-h-0" : "flex-1 min-h-0")}
      data-testid="diff-viewer">
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 px-4 py-2 border-b">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={onClose} data-testid="diff-close"
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-md font-semibold hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            <ArrowLeft className="w-4 h-4 rtl:-scale-x-100" />{documents ? "Conversation" : "Terminal"}
          </button>
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">{documents ? "Changes in this session" : "Changes on this branch"}</span>
          {index && (
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="diff-summary">
              {documents ? `${plural(index.totals.files, "document")} changed`
                : <>{plural(index.totals.files, "file")} +{int(index.totals.added)} −{int(index.totals.removed)}</>}
            </span>
          )}
        </div>
        {turnTabs.length > 1 && <Segmented label={documents ? "Version" : "Turn"} options={turnTabs} value={String(turn)} onChange={onTurn} testid="diff-turn" />}
      </div>
      {q.isLoading ? <div className="p-4"><Loading label={documents ? "Loading the changes" : undefined} /></div> : q.isError ? (
        documents ? <DocError error={q.error} onRetry={() => q.refetch()} onLines={() => {}} />
          : <div className="p-4 text-sm text-gr-danger" data-testid="diff-error">{apiErr(q.error)}</div>
      ) : (
        <div className={cn("flex-1 min-h-0 flex", documents && "flex-col md:flex-row")}>
          {documents && files.length > 0 && (
            // on a phone the documents are a dropdown above the page
            <div className="md:hidden px-4 pt-3 flex flex-col gap-1.5">
              <span className="text-sm font-semibold" id="diff-doc-label">Document</span>
              <Select value={current?.path} onValueChange={onPath}>
                <SelectTrigger aria-labelledby="diff-doc-label" data-testid="diff-doc-select">
                  <span className="truncate"><span className="font-semibold">{docName(current?.path)}</span></span>
                </SelectTrigger>
                <SelectContent>
                  {files.map((f) => <SelectItem key={f.path} value={f.path}>{docName(f.path)} · {(DOC_STATE[f.status] || "changed").replace(/^./, (c) => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <nav aria-label={documents ? "Changed documents" : "Changed files"} className={cn("shrink-0 border-e overflow-y-auto p-2 text-sm", documents ? "w-[240px] hidden md:block" : "w-[230px]")}>
            {documents && files.map((f) => (
              <button key={f.path} type="button" onClick={() => onPath(f.path)} data-testid={`diff-tree-${f.path}`}
                aria-current={f.path === current?.path ? "true" : undefined}
                className={cn("relative w-full flex items-start gap-2.5 px-2.5 py-2 rounded-md text-start hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                  f.path === current?.path && "bg-gr-hover-strong")}>
                <span className="flex-1 min-w-0 flex flex-col"><span className="font-semibold text-[13px] [overflow-wrap:anywhere]">{docName(f.path)}</span>
                  <span className="text-[11px] leading-4 text-muted-foreground [overflow-wrap:anywhere]">{docFolderLine(f.path)}</span></span>
                <DocTag state={DOC_STATE[f.status]} className="mt-0.5" />
              </button>
            ))}
            {!documents && treeOf(files).map((r) => r.dir ? (
              <div key={`d:${r.dir}`} className="px-2 pt-2 pb-0.5 font-mono text-[11px] text-muted-foreground [overflow-wrap:anywhere]">{r.dir}</div>
            ) : (
              <button key={r.file.path} type="button" onClick={() => onPath(r.file.path)} data-testid={`diff-tree-${r.file.path}`}
                aria-current={r.file.path === current?.path ? "true" : undefined}
                className={cn("relative w-full flex justify-between gap-2 py-[5px] pr-2 pl-4 rounded-md text-left hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                  r.file.path === current?.path && "bg-gr-hover-strong")}>
                <span className="font-mono text-[11px] [overflow-wrap:anywhere] min-w-0">{r.label}</span>
                <span className="whitespace-nowrap tabular-nums"><span className="text-gr-success">+{int(r.file.added)}</span><span className="text-gr-danger"> −{int(r.file.removed)}</span></span>
              </button>
            ))}
            {!files.length && <div className="p-2 text-muted-foreground">{documents ? "No documents changed in this version." : "No changes in this turn."}</div>}
            {index?.truncated && <div className="p-2 text-muted-foreground">Showing {int(files.length)} of {int(index.total_files)} files.</div>}
          </nav>
          <div className="flex-1 min-w-0 overflow-auto">
            {current && <FileDiff key={`${turn}:${current.path}`} sid={sid} token={token} turn={turn} file={current} documents={documents} versionOf={versionOf}
              mode={mode} onMode={setMode} scope={scope} onScope={setScope} branch={branch} turns={index?.turns} />}
          </div>
        </div>
      )}
    </div>
  );
}
